import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Chip,
  Paper,
} from '@mui/material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

interface Author {
  id: number;
  name: string;
  name_ku: string;
}

interface Genre {
  id: number;
  name: string;
  name_ku: string;
}

const AddBook = () => {
  const [book, setBook] = useState({
    title: '',
    description: '',
    publication_year: '',
    page_count: '',
  });

  const [authors, setAuthors] = useState<Author[]>([]);
  const [genres, setGenres] = useState<Genre[]>([]);
  const [selectedAuthors, setSelectedAuthors] = useState<number[]>([]);
  const [selectedGenres, setSelectedGenres] = useState<number[]>([]);
  const [coverImage, setCoverImage] = useState<File | null>(null);
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchAuthorsAndGenres();
  }, []);

  const fetchAuthorsAndGenres = async () => {
    try {
      const [authorsRes, genresRes] = await Promise.all([
        axios.get('/api/authors?q=all'),
        axios.get('/api/genres')
      ]);
      setAuthors(Array.isArray(authorsRes.data.authors) ? authorsRes.data.authors : []);
      setGenres(Array.isArray(genresRes.data.genres) ? genresRes.data.genres : []);
    } catch (err) {
      console.error('Error fetching data:', err);
      setAuthors([]);
      setGenres([]);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setBook(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, type: 'cover' | 'pdf') => {
    const file = e.target.files?.[0];
    if (file) {
      if (type === 'cover') setCoverImage(file);
      else setPdfFile(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const formData = new FormData();
      
      // Validate required fields
      if (!book.title) throw new Error('Title is required');
      
      // Append basic fields
      Object.entries(book).forEach(([key, value]) => {
        if (value) formData.append(key, value.toString().trim());
      });
      
      // Validate and append arrays
      if (selectedAuthors.length === 0) throw new Error('At least one author is required');
      if (selectedGenres.length === 0) throw new Error('At least one genre is required');
      
      // Send arrays as strings
      formData.append('author_ids', JSON.stringify(selectedAuthors));
      formData.append('genre_ids', JSON.stringify(selectedGenres));

      // Validate and append files
      if (!coverImage) throw new Error('Cover image is required');
      formData.append('cover_image', coverImage);

      if (!pdfFile) throw new Error('PDF file is required');
      formData.append('pdf_file', pdfFile);

      Array.from(formData.entries()).forEach(([key, value]) => {
        console.log(key, value);
      });

      const response = await axios.post('/api/books', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        }
      });

      if (response.data) {
        alert('Book added successfully!');
        navigate('/admin');
      } else {
        throw new Error('No response from server');
      }
    } catch (err) {
      console.error('Error adding book:', err);
      alert(err instanceof Error ? err.message : 'Error adding book');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom>Add New Book</Typography>
        
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Basic Information */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Title"
                name="title"
                value={book.title}
                onChange={handleInputChange}
                margin="normal"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Description"
                name="description"
                value={book.description}
                onChange={handleInputChange}
                margin="normal"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Publication Year"
                name="publication_year"
                value={book.publication_year || ''}
                onChange={handleInputChange}
                margin="normal"
                type="number"
                inputProps={{ min: 1800, max: new Date().getFullYear() }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Page Count"
                name="page_count"
                type="number"
                value={book.page_count}
                onChange={handleInputChange}
                margin="normal"
              />
            </Grid>

            {/* Authors and Genres */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Authors</InputLabel>
                <Select
                  multiple
                  value={selectedAuthors}
                  onChange={(e) => setSelectedAuthors(e.target.value as number[])}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((authorId) => (
                        <Chip
                          key={authorId}
                          label={authors.find(a => a.id === authorId)?.name}
                        />
                      ))}
                    </Box>
                  )}
                >
                  {authors.map((author) => (
                    <MenuItem key={author.id} value={author.id}>
                      {author.name} ({author.name_ku})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Genres</InputLabel>
                <Select
                  multiple
                  value={selectedGenres}
                  onChange={(e) => setSelectedGenres(e.target.value as number[])}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((genreId) => (
                        <Chip
                          key={genreId}
                          label={genres.find(g => g.id === genreId)?.name}
                        />
                      ))}
                    </Box>
                  )}
                >
                  {genres.map((genre) => (
                    <MenuItem key={genre.id} value={genre.id}>
                      {genre.name} ({genre.name_ku})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* File Uploads */}
            <Grid item xs={12} md={6}>
              <Button
                variant="contained"
                component="label"
                fullWidth
                sx={{ mt: 2 }}
              >
                Upload Cover Image
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={(e) => handleFileChange(e, 'cover')}
                />
              </Button>
              {coverImage && (
                <Chip
                  label={coverImage.name}
                  onDelete={() => setCoverImage(null)}
                  sx={{ mt: 1 }}
                />
              )}
            </Grid>

            <Grid item xs={12} md={6}>
              <Button
                variant="contained"
                component="label"
                fullWidth
                sx={{ mt: 2 }}
              >
                Upload PDF File
                <input
                  type="file"
                  hidden
                  accept=".pdf"
                  onChange={(e) => handleFileChange(e, 'pdf')}
                />
              </Button>
              {pdfFile && (
                <Chip
                  label={pdfFile.name}
                  onDelete={() => setPdfFile(null)}
                  sx={{ mt: 1 }}
                />
              )}
            </Grid>

            {/* Submit Button */}
            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                size="large"
                disabled={loading}
                sx={{ mt: 3 }}
              >
                {loading ? 'Adding Book...' : 'Add Book'}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Container>
  );
};

export default AddBook; 